import { IconButton, InventoryNumber } from '@artosera/ui';
import { GripVertical, X } from 'lucide-react';
import type { DragEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArtistes } from '../../../api/artistes.js';
import {
  useModifierExposition,
  useOrdonnerOeuvresExposition,
  type ArtisteDeLExposition,
  type ExpositionPresentee,
  type OeuvreDeLExposition,
} from '../../../api/expositions.js';
import { useOeuvres } from '../../../api/oeuvres.js';
import { useDebounce } from '../../oeuvres/useDebounce.js';
import styles from './Tabs.module.css';

export interface OeuvresArtistesTabProps {
  galerieSlug: string;
  exposition: ExpositionPresentee;
  artistes: readonly ArtisteDeLExposition[];
  oeuvres: readonly OeuvreDeLExposition[];
  peutEcrire: boolean;
}

/**
 * Œuvres et artistes (3.3, point 4, S3-d) : ajout par recherche depuis les
 * listes existantes, retrait, ordre. L'accrochage (les œuvres) passe par la
 * route dédiée `PUT …/expositions/:id/oeuvres` (§ 15.1) ; les artistes,
 * comme leur biographie, par `PATCH …/expositions/:id`.
 */
export function OeuvresArtistesTab({
  galerieSlug,
  exposition,
  artistes,
  oeuvres,
  peutEcrire,
}: OeuvresArtistesTabProps): JSX.Element {
  return (
    <div className={styles.largeur}>
      <ArtistesSection
        galerieSlug={galerieSlug}
        exposition={exposition}
        artistes={artistes}
        peutEcrire={peutEcrire}
      />
      <OeuvresSection
        galerieSlug={galerieSlug}
        exposition={exposition}
        oeuvres={oeuvres}
        peutEcrire={peutEcrire}
      />
    </div>
  );
}

function ArtistesSection({
  galerieSlug,
  exposition,
  artistes,
  peutEcrire,
}: {
  galerieSlug: string;
  exposition: ExpositionPresentee;
  artistes: readonly ArtisteDeLExposition[];
  peutEcrire: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [glisseId, setGlisseId] = useState<string | null>(null);

  const modifier = useModifierExposition(galerieSlug);
  const resultats = useArtistes(galerieSlug, {
    q: rechercheDebattue || undefined,
    tri: 'nom',
    parPage: 20,
  });

  const idsActuels = artistes.map((artiste) => artiste.id);

  function ajouter(id: string): void {
    if (idsActuels.includes(id)) return;
    modifier.mutate({ id: exposition.id, corps: { artistes: [...idsActuels, id] } });
  }

  function retirer(id: string): void {
    modifier.mutate({
      id: exposition.id,
      corps: { artistes: idsActuels.filter((candidat) => candidat !== id) },
    });
  }

  function deplacer(cibleId: string): void {
    if (!glisseId || glisseId === cibleId) return;
    const ids = [...idsActuels];
    const depart = ids.indexOf(glisseId);
    const arrivee = ids.indexOf(cibleId);
    if (depart === -1 || arrivee === -1) return;
    ids.splice(arrivee, 0, ...ids.splice(depart, 1));
    modifier.mutate({ id: exposition.id, corps: { artistes: ids } });
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitre}>{t('regie:expositions.oeuvresTab.artistesTitre')}</h2>

      {artistes.length === 0 ? (
        <p className={styles.vide}>{t('regie:expositions.oeuvresTab.aucunArtiste')}</p>
      ) : (
        <ul className={styles.liste}>
          {artistes.map((artiste) => (
            <li
              key={artiste.id}
              className={styles.ligne}
              data-glisse={glisseId === artiste.id ? 'true' : 'false'}
              draggable={peutEcrire}
              onDragStart={() => setGlisseId(artiste.id)}
              onDragEnd={() => setGlisseId(null)}
              onDragOver={(event: DragEvent) => event.preventDefault()}
              onDrop={() => deplacer(artiste.id)}
            >
              {peutEcrire ? (
                <GripVertical className={styles.poignee} size={16} strokeWidth={1.5} aria-hidden />
              ) : null}
              <span className={styles.ligneTitre}>{artiste.nom}</span>
              {peutEcrire ? (
                <IconButton
                  variant="outline"
                  icon={<X aria-hidden />}
                  aria-label={t('regie:expositions.oeuvresTab.retirer')}
                  size="sm"
                  onClick={() => retirer(artiste.id)}
                />
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {peutEcrire ? (
        <div className={styles.recherche}>
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:expositions.oeuvresTab.rechercheArtiste')}
            aria-label={t('regie:expositions.oeuvresTab.rechercheArtiste')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
          {recherche.trim().length > 0 ? (
            <div className={styles.rechercheResultats}>
              {(resultats.data?.artistes ?? []).map((artiste) => {
                const dejaAjoute = idsActuels.includes(artiste.id);
                return (
                  <button
                    key={artiste.id}
                    type="button"
                    className={styles.rechercheLigne}
                    data-ajoutee={dejaAjoute ? 'true' : 'false'}
                    disabled={dejaAjoute}
                    onClick={() => ajouter(artiste.id)}
                  >
                    <span>{artiste.nom}</span>
                    {dejaAjoute ? (
                      <span>{t('regie:expositions.oeuvresTab.dejaAjoutee')}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function OeuvresSection({
  galerieSlug,
  exposition,
  oeuvres,
  peutEcrire,
}: {
  galerieSlug: string;
  exposition: ExpositionPresentee;
  oeuvres: readonly OeuvreDeLExposition[];
  peutEcrire: boolean;
}): JSX.Element {
  const { t } = useTranslation(['regie', 'oeuvres', 'commun']);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [glisseId, setGlisseId] = useState<string | null>(null);

  const ordonner = useOrdonnerOeuvresExposition(galerieSlug);
  const resultats = useOeuvres(galerieSlug, {
    q: rechercheDebattue || undefined,
    parPage: 20,
  });

  const idsActuels = oeuvres.map((oeuvre) => oeuvre.id);

  function ajouter(id: string): void {
    if (idsActuels.includes(id)) return;
    ordonner.mutate({ id: exposition.id, oeuvres: [...idsActuels, id] });
  }

  function retirer(id: string): void {
    ordonner.mutate({
      id: exposition.id,
      oeuvres: idsActuels.filter((candidat) => candidat !== id),
    });
  }

  function deplacer(cibleId: string): void {
    if (!glisseId || glisseId === cibleId) return;
    const ids = [...idsActuels];
    const depart = ids.indexOf(glisseId);
    const arrivee = ids.indexOf(cibleId);
    if (depart === -1 || arrivee === -1) return;
    ids.splice(arrivee, 0, ...ids.splice(depart, 1));
    ordonner.mutate({ id: exposition.id, oeuvres: ids });
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitre}>{t('regie:expositions.oeuvresTab.oeuvresTitre')}</h2>
      <p className={styles.aide}>{t('regie:expositions.oeuvresTab.ordreAide')}</p>

      {oeuvres.length === 0 ? (
        <p className={styles.vide}>{t('regie:expositions.oeuvresTab.aucuneOeuvre')}</p>
      ) : (
        <ol className={`${styles.liste} ${styles.accrochage}`}>
          {oeuvres.map((oeuvre) => (
            <li
              key={oeuvre.id}
              className={styles.ligne}
              data-glisse={glisseId === oeuvre.id ? 'true' : 'false'}
              draggable={peutEcrire}
              onDragStart={() => setGlisseId(oeuvre.id)}
              onDragEnd={() => setGlisseId(null)}
              onDragOver={(event: DragEvent) => event.preventDefault()}
              onDrop={() => deplacer(oeuvre.id)}
            >
              {peutEcrire ? (
                <GripVertical className={styles.poignee} size={16} strokeWidth={1.5} aria-hidden />
              ) : null}
              <span className={styles.rang} aria-hidden />
              <div className={styles.ligneInfos}>
                <span className={styles.titreOeuvre}>{oeuvre.titre}</span>
                <span className={styles.ligneMeta}>
                  {oeuvre.artisteNom} · <InventoryNumber value={oeuvre.numeroInventaire} />
                </span>
              </div>
              {peutEcrire ? (
                <IconButton
                  variant="outline"
                  icon={<X aria-hidden />}
                  aria-label={t('regie:expositions.oeuvresTab.retirer')}
                  size="sm"
                  onClick={() => retirer(oeuvre.id)}
                />
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {peutEcrire ? (
        <div className={styles.recherche}>
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:expositions.oeuvresTab.rechercheOeuvre')}
            aria-label={t('regie:expositions.oeuvresTab.rechercheOeuvre')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
          {recherche.trim().length > 0 ? (
            <div className={styles.rechercheResultats}>
              {(resultats.data?.oeuvres ?? []).map((oeuvre) => {
                const dejaAjoutee = idsActuels.includes(oeuvre.id);
                return (
                  <button
                    key={oeuvre.id}
                    type="button"
                    className={styles.rechercheLigne}
                    data-ajoutee={dejaAjoutee ? 'true' : 'false'}
                    disabled={dejaAjoutee}
                    onClick={() => ajouter(oeuvre.id)}
                  >
                    <span>
                      {oeuvre.titre} — {oeuvre.artisteNom}
                    </span>
                    {dejaAjoutee ? (
                      <span>{t('regie:expositions.oeuvresTab.dejaAjoutee')}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
